<template>
  <div
    :id="modalId"
    class="modal fade"
    ref="modalRef"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header d-flex justify-content-between align-items-center">
          <h3 class="modal-title m-0">
            <strong>{{ title }}</strong>
          </h3>
          <button type="button" class="close btn-link" data-bs-dismiss="modal" aria-label="Close">
            <span>&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <!-- Private item: prompt to make public -->
          <template v-if="isPrivate">
            <p class="mb-2">
              <strong>This {{ itemTypeLabel }} is private</strong>
            </p>
            <p class="mb-3 text-muted">
              <small>You need to make this {{ itemTypeLabel }} public before you can share it.</small>
            </p>

            <!-- Email not confirmed: block with resend option -->
            <template v-if="!isEmailConfirmed">
              <div class="card p-3 mb-3">
                <small class="d-block mb-2">
                  You must confirm your email before making {{ itemTypeLabel }}s public.
                </small>
                <button
                  class="btn btn-link p-0"
                  type="button"
                  @click.prevent="resendConfirmationEmail"
                  :disabled="resendLoading"
                >
                  <small>{{ resendLoading ? 'Sending...' : 'Resend confirmation email' }}</small>
                </button>
                <small v-if="resendStatus" class="d-block text-muted mt-1">{{ resendStatus }}</small>
              </div>
            </template>

            <!-- Email confirmed: allow making public -->
            <template v-else>
              <button
                class="btn btn-primary"
                type="button"
                :disabled="makingPublic"
                @click="makePublicAndShare"
              >
                <span v-if="makingPublic" class="spinner-border spinner-border-sm me-1"></span>
                Make Public &amp; Share
              </button>
              <small v-if="makePublicError" class="d-block text-danger mt-2">{{ makePublicError }}</small>
            </template>
          </template>

          <!-- Public item: show share link -->
          <template v-else>
            <p class="mb-1">
              <strong>{{ label }}</strong>
              <span v-if="copied" class="text-yellow">(Copied!)</span>
            </p>
            <p class="mb-3">
              <small class="text-muted">{{ helperText }}</small>
            </p>

            <div class="input-group mb-3">
              <input
                ref="inputRef"
                class="form-control"
                type="text"
                :value="currentLink"
                readonly
                @focus="$event.target.select()"
              />
              <button class="btn btn-primary input-group-text" type="button" @click="copyToClipboard">
                <span class="material-icons">content_copy</span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Modal } from 'bootstrap';
import graph_client from '../graphql/graph_client';
import { UpdateHikeDetails } from '../graphql/hikes-graph';
import { UpdateRecording } from '../graphql/recorded-trail-graph';

interface Props {
  modalId?: string;
  title?: string;
  label?: string;
  helperText?: string;
  defaultLink?: string;
}

const props = defineProps<Props>();

const modalRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const modalInstance = ref<Modal | null>(null);
const currentLink = ref<string>(props.defaultLink || window.location.href);
const copied = ref(false);

// Privacy-aware state
const isPrivate = ref(false);
const currentItem = ref<any>(null);
const currentItemType = ref<'hike' | 'recording' | null>(null);
const itemTypeLabel = ref('hike');
const isEmailConfirmed = !!(window as any).user && !!(window as any).user.confirmed_at;
const makingPublic = ref(false);
const makePublicError = ref('');
const resendLoading = ref(false);
const resendStatus = ref('');

const show = async (link?: string, item?: any, itemType?: 'hike' | 'recording') => {
  copied.value = false;
  makingPublic.value = false;
  makePublicError.value = '';
  resendStatus.value = '';
  currentLink.value = link || props.defaultLink || window.location.href;
  currentItem.value = item || null;
  currentItemType.value = itemType || null;
  itemTypeLabel.value = itemType === 'recording' ? 'recording' : 'hike';

  // Determine if item is private
  if (item && itemType === 'hike') {
    isPrivate.value = item.publiclyVisible === false;
  } else if (item && itemType === 'recording') {
    isPrivate.value = item.public === false;
  } else {
    isPrivate.value = false;
  }

  await nextTick();
  if (inputRef.value) {
    inputRef.value.value = currentLink.value;
  }
  modalInstance.value?.show();
};

const hide = () => {
  modalInstance.value?.hide();
};

const copyToClipboard = async () => {
  const text = currentLink.value;
  try {
    await navigator.clipboard.writeText(text);
    copied.value = true;
  } catch (error) {
    if (inputRef.value) {
      inputRef.value.select();
      document.execCommand('copy');
      copied.value = true;
    }
  }
};

async function makePublicAndShare() {
  if (!currentItem.value || !currentItemType.value) return;
  makingPublic.value = true;
  makePublicError.value = '';

  try {
    if (currentItemType.value === 'hike') {
      const result = await graph_client.request(UpdateHikeDetails, {
        input: {
          id: currentItem.value.id,
          publiclyVisible: true,
        },
      });
      const errors = result?.updateHikeDetails?.errors;
      if (errors && errors.length > 0) {
        const msgs = errors.flatMap((e: any) => e.errors || []);
        throw new Error(msgs.join(', ') || 'Failed to update hike');
      }
      currentItem.value.publiclyVisible = true;
    } else {
      await graph_client.request(UpdateRecording, {
        input: {
          id: currentItem.value.id,
          public: true,
        },
      });
      currentItem.value.public = true;
    }
    isPrivate.value = false;
  } catch (error: any) {
    makePublicError.value = error?.message || 'Something went wrong. Please try again.';
  } finally {
    makingPublic.value = false;
  }
}

async function resendConfirmationEmail() {
  if (resendLoading.value) return;
  resendLoading.value = true;
  resendStatus.value = '';
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const response = await fetch('/user/resend_confirmation', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken || '', 'Accept': 'application/json' },
    });
    const data = await response.json();
    resendStatus.value = data?.message || 'Confirmation email sent.';
  } catch {
    resendStatus.value = 'Could not send confirmation email. Please try again.';
  } finally {
    resendLoading.value = false;
  }
}

defineExpose({ show, hide });

onMounted(() => {
  if (modalRef.value) {
    modalInstance.value = Modal.getOrCreateInstance(modalRef.value);
    modalRef.value.addEventListener('hidden.bs.modal', () => {
      copied.value = false;
    });
  }
});

onBeforeUnmount(() => {
  modalInstance.value?.hide();
  modalInstance.value?.dispose?.();
  modalInstance.value = null;
});

const modalId = props.modalId || 'shareLinkModal';
const title = props.title || 'Share';
const label = props.label || 'Share Link';
const helperText = props.helperText || 'Copy and share this link';

</script>
